import React from 'react';
import styles from './AnalysisTreePanel.module.css';
import Input from '../Input/Input';
import { IconButton } from '../IconButton/IconButton';

export interface TreeNode {
  id: string;
  label: string;
  icon?: string;
  type: 'item' | 'step' | 'element';
  badges?: React.ReactNode;
  statusIcon?: React.ReactNode;
  children?: TreeNode[];
  parentId?: string;
}

interface AnalysisTreePanelProps {
  /** Master title for the whole section */
  masterTitle?: string;
  /** Nodes to display in the tree */
  nodes: TreeNode[];
  /** ID of the currently selected node */
  selectedNodeId?: string | null;
  /** Callback when a node is selected */
  onSelectNode: (nodeId: string, type: string) => void;
  /** Content to display in the detail column */
  detailContent: React.ReactNode;
  /** Label for the search bar */
  searchLabel: string;
  /** Current search term */
  searchTerm: string;
  /** Callback on search change */
  onSearchChange: (value: string) => void;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Message to show when no node is selected */
  emptySelectionMessage?: string;
  /** Message to show when no nodes match search */
  emptyMessage?: string;
  /** Loading state */
  loading?: boolean;
  /** Optional validation or pending message */
  validationMessage?: string | null;
  /** Optional class name */
  className?: string;
}

/**
 * AnalysisTreePanel component provides a standardized split-panel layout for FMEA analysis steps.
 * It features a searchable structure tree on the left and a detail/action column on the right.
 */
export const AnalysisTreePanel: React.FC<AnalysisTreePanelProps> = ({
  masterTitle,
  nodes,
  selectedNodeId,
  onSelectNode,
  detailContent,
  searchLabel,
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  emptySelectionMessage = "Selecione um item para ver os detalhes",
  emptyMessage = "Nenhum resultado encontrado",
  loading,
  validationMessage,
  className = '',
}) => {
  const renderIcon = (name: string, filled = false): React.ReactNode => (
    <span
      className="material-symbols-outlined"
      aria-hidden="true"
      style={{ fontSize: "inherit", fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0" }}
    >
      {name}
    </span>
  );

  const renderNode = (node: TreeNode, level = 0): React.ReactNode => {
    const isSelected = selectedNodeId === node.id;
    const hasChildren = node.children && node.children.length > 0;
    
    return (
      <div key={node.id} className={`${styles.treeNodeWrapper} ${level > 0 ? styles.childNode : styles.parentNode}`}>
        <button
          type="button"
          className={`${styles.treeNode} ${isSelected ? styles.selected : ''} ${node.type === 'item' ? styles.itemRoot : ''}`}
          onClick={() => onSelectNode(node.id, node.type)}
        >
          <div className={styles.nodeIcon}>
            {renderIcon(node.icon || (node.type === 'item' ? 'inventory_2' : node.type === 'step' ? 'account_tree' : 'settings_input_component'), isSelected)}
          </div>
          <span className={styles.nodeLabel}>{node.label}</span>
          {node.statusIcon && (
            <div className={styles.statusIcon}>
              {node.statusIcon}
            </div>
          )}
          {node.badges && <div className={styles.nodeBadges}>{node.badges}</div>}
        </button>
        {hasChildren && (
          <div className={styles.children}>
            {node.children!.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const filteredNodes = searchTerm 
    ? nodes.map(n => ({
        ...n,
        children: n.children?.filter(c => 
          c.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
          c.children?.some(gc => gc.label.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      })).filter(n => 
        n.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (n.children && n.children.length > 0)
      )
    : nodes;

  return (
    <div className={`${styles.container} ${className}`}>
      {masterTitle && (
        <header className={styles.masterHeader}>
          <h2 className={styles.masterTitle}>{masterTitle}</h2>
        </header>
      )}

      <div className={styles.layout}>
        <aside className={styles.structureColumn}>
          <div className={styles.structureCard}>
            <div className={styles.searchBar}>
              <label className={styles.searchTitle}>{searchLabel}</label>
              <div className={styles.searchInputWrapper}>
                <div className={styles.searchIcon}>
                  {renderIcon('search')}
                </div>
                <Input
                  type="text"
                  label=""
                  value={searchTerm}
                  onChange={onSearchChange}
                  placeholder={searchPlaceholder}
                  className={styles.searchInput}
                />
                {searchTerm && (
                  <div className={styles.clearButton}>
                    <IconButton
                      icon="close"
                      variant="text"
                      severity="secondary"
                      aria-label="Clear"
                      onClick={() => onSearchChange('')}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className={styles.structureContent}>
              {loading ? (
                <div className={styles.loadingState}>
                  {renderIcon('sync')}
                  <span>Carregando...</span>
                </div>
              ) : filteredNodes.length === 0 ? (
                <div className={styles.emptyState}>
                  {renderIcon('search_off')}
                  <p>{emptyMessage}</p>
                </div>
              ) : (
                <div className={styles.tree}>
                  {filteredNodes.map(node => renderNode(node))}
                </div>
              )}
            </div>
          </div>
        </aside>
        
        <main className={styles.detailColumn}>
          <div className={styles.detailCard}>
            {selectedNodeId ? detailContent : (
              <div className={styles.emptySelection}>
                {renderIcon('info')}
                <p>{emptySelectionMessage}</p>
              </div>
            )}
          </div>
          {validationMessage && (
            <span className={styles.validationNote}>
              {validationMessage}
            </span>
          )}
        </main>
      </div>
    </div>
  );
};

export default AnalysisTreePanel;
